// src/features/files/components/DeleteFilesDialog.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

import { filesAPI } from '../services/files';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

/** "20 sikkerhedsdatablade, 1 opgave (manual)" for one file's usage. */
export function useUsageText() {
  const { t } = useTranslation(['files']);
  return (usages) => usages.map(u => t(`files:usage.kinds.${u.kind}`, { count: u.count })).join(', ');
}

/**
 * Confirms deleting one or more files. It first asks the server where each file
 * is used: files still used by equipment, tasks, safety data sheets etc. can't be
 * deleted (the server refuses too) and are listed with where they're used; the
 * rest are deleted for good on confirm. `onDeleted(fileIds)` gets the deleted ones.
 */
export default function DeleteFilesDialog({ open, files = [], onClose, onDeleted }) {
  const { t } = useTranslation(['files', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const usageText = useUsageText();
  const [usage, setUsage] = useState(null); // { [file_id]: usages[] }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !files.length) return undefined;
    let cancelled = false;
    setUsage(null);
    Promise.all(files.map(file => filesAPI.getFileUsage(file.file_id)
      .then(res => [file.file_id, res.data?.data?.usages || []])
      // If the check fails, the server still refuses deleting a file in use
      .catch(() => [file.file_id, []])))
      .then((entries) => { if (!cancelled) setUsage(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [open, files]);

  const inUse = usage ? files.filter(f => usage[f.file_id]?.length) : [];
  const deletable = usage ? files.filter(f => !usage[f.file_id]?.length) : [];
  const single = files.length === 1;

  const handleDelete = async () => {
    setDeleting(true);
    const deleted = [];
    let failed = 0;
    for (const file of deletable) {
      try {
        await filesAPI.deleteFile(file.file_id);
        deleted.push(file.file_id);
      } catch {
        failed += 1;
      }
    }
    setDeleting(false);
    if (deleted.length) showSuccess(t('files:delete.done', { count: deleted.length }));
    if (failed) showError(t('files:delete.failed', { count: failed }));
    onDeleted?.(deleted);
    onClose();
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {single
          ? t('files:delete.title_one', { name: files[0]?.filename })
          : t('files:delete.title_other', { count: files.length })}
      </DialogTitle>
      <DialogContent>
        {!usage ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {inUse.length > 0 && (
              <Alert severity="warning" sx={{ mb: deletable.length ? 2 : 0 }}>
                {single ? t('files:delete.in_use_one') : t('files:delete.in_use_other')}
                <List dense disablePadding sx={{ mt: 0.5 }}>
                  {inUse.map(file => (
                    <ListItem key={file.file_id} disableGutters sx={{ py: 0 }}>
                      <ListItemText
                        primary={single ? null : file.filename}
                        secondary={t('files:delete.used_by', { places: usageText(usage[file.file_id]) })}
                        slotProps={{ secondary: { color: 'inherit' } }}
                      />
                    </ListItem>
                  ))}
                </List>
                {t('files:delete.remove_first', { count: inUse.length })}
              </Alert>
            )}
            {deletable.length > 0 && (
              <DialogContentText>
                {t('files:delete.permanent', { count: deletable.length })}
              </DialogContentText>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleting}>
          {deletable.length || !usage ? t('common:buttons.cancel') : t('common:buttons.close')}
        </Button>
        {(deletable.length > 0 || !usage) && (
          <Button
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            onClick={handleDelete}
            disabled={!usage || deleting}
          >
            {single ? t('common:buttons.delete') : t('files:delete.button', { count: deletable.length })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
